import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Form, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { suggest, organizationPath } from '../../helpers/findApi';

const MAIN_TYPE = { AG: 'Auftraggeber', AN: 'Auftragnehmer', Berater: 'Berater', Anwalt: 'Anwalt', ZT: 'Ziviltechniker' };

/** Placeholders from the longest to the shortest; the input shows the longest that fits its width. */
const PLACEHOLDERS = [
  'Suchen nach Organisationen, Veröffentlichungen und Dokumenten',
  'Organisationen, Veröffentlichungen, Dokumente',
  'Suchen',
];

/** The longest placeholder that fits the input, measured in the input's own font and padding. */
function usePlaceholder(ref) {
  const [text, setText] = useState(PLACEHOLDERS[0]);
  useLayoutEffect(() => {
    const input = ref.current;
    if (!input) return undefined;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const fit = () => {
      const style = window.getComputedStyle(input);
      ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const room = input.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - 4;
      // When not even the shortest fits, the magnifier alone marks the field.
      setText(PLACEHOLDERS.find((p) => ctx.measureText(p).width <= room) || '');
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(input);
    return () => observer.disconnect();
  }, [ref]);
  return text;
}

/**
 * The search bar: organizations as you type, each to its page; Enter searches organizations,
 * publications and documents on /suche.
 */
const FindBox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState(() => (location.pathname === '/suche' ? new URLSearchParams(location.search).get('q') || '' : ''));
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef();
  const inputRef = useRef(null);
  const placeholder = usePlaceholder(inputRef);

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setOptions([]);
      return;
    }
    timer.current = window.setTimeout(() => suggest(q).then(setOptions).catch(() => setOptions([])), 200);
    return () => window.clearTimeout(timer.current);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    navigate(`/suche?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <Dropdown isOpen={open && options.length > 0} toggle={() => setOpen(!open)} className="search-box w-100 mw-100">
      <DropdownToggle tag="div" data-toggle="dropdown" aria-expanded={open} className="w-100">
        <Form inline className="search-box w-100 mw-100" onSubmit={submit}>
          <FontAwesomeIcon icon="search" className="position-absolute text-400 search-box-icon" />
          <Input
            innerRef={inputRef}
            type="search"
            placeholder={placeholder}
            aria-label="Suche"
            className="rounded-pill search-input w-100"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onClick={(e) => e.stopPropagation()}
          />
        </Form>
      </DropdownToggle>
      <DropdownMenu className="w-100">
        <div className="scrollbar py-2" style={{ maxHeight: '24rem' }}>
          {options.map((o) => (
            <DropdownItem key={o.iri} tag={Link} to={organizationPath(o.iri, o.mainType)} className="px-card py-2" onClick={() => setOpen(false)}>
              <h6 className="mb-0">{o.name}</h6>
              <small className="text-500">{[MAIN_TYPE[o.mainType] || o.mainType, o.seat].filter(Boolean).join(' · ')}</small>
            </DropdownItem>
          ))}
          <DropdownItem divider />
          <DropdownItem onClick={submit} className="px-card">Alle Treffer zu „{q.trim()}“</DropdownItem>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

export default FindBox;
